import { useState, useEffect } from "react";
import { generatePlans } from "@/lib/scheduler";
import type { Course, Plan } from "@/types";
import { toast } from "sonner";
import { useLocalStorage } from "@/hooks/useLocalStorage";

/**
 * The live schedule-building session: the courses on the table, which codes
 * are selected, which specific classes are locked, and the generated plans.
 *
 * This used to be ~20 separate useState calls and their handlers inlined
 * directly in ScheduleMaker.tsx alongside unrelated concerns (sharing, smart
 * generate, the tutorial). Pulling it into one hook does not shrink the
 * amount of logic, but it does separate "building a schedule" from
 * "everything else the maker screen happens to also do" -- the two were
 * previously impossible to tell apart by reading the component.
 */

interface UseScheduleSessionArgs {
  t: (key: string, vars?: Record<string, string | number>) => string;
  setStep: (step: "config" | "select" | "view" | "archive") => void;
  userData?: { planLimit?: number; preferredAiModel?: string };
  savePlan: (args: {
    name: string;
    plan: Plan;
    isSmartGenerated?: boolean;
    generatedBy?: string;
  }) => Promise<string>;
  isLocalArchive: boolean;
  /** Which prodi+semester the persisted courses/selectedCodes/lockedCourses
   * actually belong to. When this drifts from what was last recorded, those
   * three are cleared automatically -- otherwise a config change left stale,
   * wrong-prodi courses sitting in the select step with no way back to an
   * empty state short of the explicit "Hapus Sesi" action. */
  configKey: string;
  /** Called after a plan save when the user's total save count is a
   * multiple of 5 (5th, 10th, 15th...). Fires at most once per milestone. */
  onFeedbackTrigger?: (saveCount: number) => void;
  /** Active session profile, used to stamp prodi/semester on saved plans. */
  sessionProfile: { prodi: string; semester: number };
}

export function useScheduleSession({
  t,
  setStep,
  userData,
  savePlan,
  isLocalArchive,
  configKey,
  onFeedbackTrigger,
  sessionProfile,
}: UseScheduleSessionArgs) {
  const [courses, setCourses] = useLocalStorage<Course[]>("krs-courses", []);
  const [selectedCodes, setSelectedCodes] = useLocalStorage<string[]>(
    "krs-selected-codes",
    [],
  );
  const [lockedCourses, setLockedCourses] = useLocalStorage<
    Record<string, string[]>
  >("krs-locked-courses", {});
  const [coursesConfigKey, setCoursesConfigKey] = useLocalStorage<
    string | null
  >("krs-courses-config-key", null);

  useEffect(() => {
    if (coursesConfigKey === null) {
      // First run (or post-clear): just record what config the current
      // (empty) state belongs to, nothing to wipe yet.
      setCoursesConfigKey(configKey);
      return;
    }
    if (coursesConfigKey !== configKey) {
      setCourses([]);
      setSelectedCodes([]);
      setLockedCourses({});
      setCoursesConfigKey(configKey);
    }
    // useLocalStorage's setters are stable per key (useCallback keyed only
    // on the storage key itself), so including them here does not cause this
    // to re-run on every unrelated courses/selectedCodes update -- only
    // configKey actually changing drives it.
  }, [
    configKey,
    coursesConfigKey,
    setCourses,
    setSelectedCodes,
    setLockedCourses,
    setCoursesConfigKey,
  ]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentPlanIndex, setCurrentPlanIndex] = useState(0);
  const [viewSource, setViewSource] = useState<"live" | "archive">("live");
  const [planLimit, setPlanLimit] = useState(userData?.planLimit || 12);
  const [maxDailySks, setMaxDailySks] = useState(8);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isManualMode, setIsManualMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const toggleCourse = (code: string) => {
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const handleDeleteCourse = (e: React.MouseEvent, code: string) => {
    e.stopPropagation();
    // Removes every row for this code, not just the currently-active variant.
    // handleAutoLoad adds every class/section master_courses has for a code,
    // so a course with multiple sections had siblings left behind when this
    // only matched by id -- the row kept reappearing (via the next available
    // variation) and looked like delete had silently failed.
    setCourses((prev) => prev.filter((c) => c.code !== code));
    setSelectedCodes((prev) => prev.filter((c) => c !== code));
    setLockedCourses((prev: any) => {
      const next = { ...prev };
      delete next[code];
      return next;
    });
    toast.success(t("toast.course_removed"));
  };

  const handleAutoLoad = (
    curriculum: { code: string }[] | undefined,
    allMasterCourses: any[] | undefined,
  ) => {
    if (!curriculum || !allMasterCourses) return;
    const mandatoryCodes = new Set(curriculum.map((c) => c.code));
    const filteredCourses = allMasterCourses.filter((c) =>
      mandatoryCodes.has(c.code),
    );

    const coursesWithIds = filteredCourses.map((c: any) => ({
      ...c,
      id: c._id || `${c.code}-${c.class}`,
    }));

    // The toast used to report mandatoryCodes.size -- the number of curriculum
    // rows -- regardless of whether any matching class was actually found in
    // master_courses. A curriculum can exist for a prodi+semester before its
    // sections are imported, so that count was frequently a lie ("N mata
    // kuliah dimuat" when 0 courses actually loaded). Report and select only
    // codes that resolved to a real course.
    const foundCodes = new Set(coursesWithIds.map((c: any) => c.code));

    setCourses(coursesWithIds as any);
    setSelectedCodes(Array.from(foundCodes));
    setStep("select");
    setPlanLimit(12);

    if (foundCodes.size === 0) {
      toast.warning(t("toast.curriculum_empty"));
    } else {
      toast.success(t("toast.curriculum_loaded", { count: foundCodes.size }));
    }
  };

  const handleAddMultipleMasterCourses = (
    masterCourses: any[],
    onDone: () => void,
  ) => {
    const newCourses = masterCourses.map((mc) => ({
      ...mc,
      id: mc._id || `${mc.code}-${mc.class}`,
    }));

    setCourses((prev) => [...prev, ...newCourses]);

    const uniqueNewCodes = [
      ...new Set(masterCourses.map((mc) => mc.code)),
    ].filter((code) => !selectedCodes.includes(code));

    if (uniqueNewCodes.length > 0) {
      setSelectedCodes((prev) => [...prev, ...uniqueNewCodes]);
    }

    // Count/SKS by unique course code, not by row -- masterCourses can carry
    // every class/section for a code, and the toast should read as "N mata
    // kuliah" (courses), matching what the catalog dialog's own selection
    // badges already count, not "N sections."
    const seenCodes = new Set<string>();
    let totalSks = 0;
    for (const mc of masterCourses) {
      if (!seenCodes.has(mc.code)) {
        seenCodes.add(mc.code);
        totalSks += mc.sks || 0;
      }
    }

    toast.success(
      t("toast.courses_added", { count: seenCodes.size, sks: totalSks }),
    );
    onDone();
  };

  const handleGenerate = async (
    tokenized: boolean,
    deps: {
      requireAuth: (reason: string) => boolean;
      consumeTokenMutation: (args: { type: string }) => Promise<unknown>;
    },
  ) => {
    let currentLimit = Math.max(planLimit, userData?.planLimit || 12);

    if (tokenized) {
      if (
        !deps.requireAuth(
          t("auth.expand_plan"),
        )
      ) {
        return;
      }
      if (currentLimit >= 36) {
        toast.error(t("toast.plan_limit"));
        return;
      }
      if (!userData || (userData as any).credits <= 0) {
        toast.error(t("toast.daily_limit"));
        return;
      }
      try {
        await deps.consumeTokenMutation({ type: "expand" });
        toast.success(t("toast.token_spent"));
        currentLimit = Math.min((userData?.planLimit || planLimit) + 12, 36);
        setPlanLimit(currentLimit);
      } catch (err: any) {
        toast.error(t("toast.token_failed", { error: err.message }));
        return;
      }
    }

    setIsGenerating(true);
    try {
      const activeCourses = courses.filter((c) => {
        if (!selectedCodes.includes(c.code)) return false;
        const lockedIds = lockedCourses[c.code];
        if (!lockedIds || lockedIds.length === 0) return true;
        return lockedIds.includes(c.id);
      });

      if (activeCourses.length === 0) {
        toast.error(t("toast.no_courses"));
        return;
      }

      const generated = generatePlans(
        activeCourses,
        selectedCodes,
        currentLimit,
        maxDailySks,
      );

      if (generated.length === 0) {
        toast.error(t("toast.no_valid_schedules"));
        return;
      }

      if (tokenized) {
        const existingComboKeys = new Set(
          plans.map((p) =>
            p.courses
              .map((c) => c.id)
              .sort()
              .join(","),
          ),
        );

        const newPlans = generated
          .filter((p) => {
            const key = p.courses
              .map((c) => c.id)
              .sort()
              .join(",");
            return !existingComboKeys.has(key);
          })
          .slice(0, 12);

        if (newPlans.length === 0) {
          toast.info(t("toast.no_combinations"));
        } else {
          setPlans((prev) => [...prev, ...newPlans]);
          toast.success(t("toast.plans_imported", { count: newPlans.length }));
        }
      } else {
        setPlans(generated);
        setCurrentPlanIndex(0);
        setViewSource("live");
        setIsManualMode(false);
        setStep("view");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveManualPlan = async (data: Course[] | Plan) => {
    if (Array.isArray(data) && data.length === 0) {
      const draftCombo = selectedCodes
        .map((code) => {
          const variations = courses.filter((c) => c.code === code);
          const lockedIds = lockedCourses[code] || [];
          return variations.find((v) => lockedIds.includes(v.id)) || null;
        })
        .filter(Boolean);

      const draftPlan: Plan = {
        id: "manual-draft",
        name: "Manual Draft",
        courses: draftCombo as Course[],
        score: { safe: 100, risky: 0, optimal: 0 },
        analysis: "Manual assembly in progress...",
      };

      setPlans([draftPlan]);
      setCurrentPlanIndex(0);
      setIsManualMode(true);
      setStep("view");
      setViewSource("live");
      return;
    }

    setIsSaving(true);
    try {
      const isFullPlan = !Array.isArray(data);
      const name = isFullPlan
        ? data.name
        : `Manual Draft ${new Date().toLocaleTimeString()}`;

      // Stamp prodi & semester so "Muat & Edit" can later reconstruct the full
      // catalog inventory for this plan without bloating the payload.
      const profileMeta: Pick<Plan, "prodi" | "semester"> = {
        prodi: sessionProfile.prodi,
        semester: sessionProfile.semester,
      };
      const payload = isFullPlan
        ? { ...data, ...profileMeta }
        : {
            id: crypto.randomUUID(),
            name: "Manual Plan",
            courses: data,
            score: { safe: 100, risky: 0, optimal: 0 },
            analysis: "Hand-crafted schedule with manual selection",
            ...profileMeta,
          };

      const planId = await savePlan({
        name,
        plan: payload as Plan,
        isSmartGenerated: isFullPlan,
        generatedBy: isFullPlan
          ? userData?.preferredAiModel || "groq"
          : "manual",
      });

      toast.success(
        t(isFullPlan ? "toast.plan_archived" : "toast.manual_saved"),
        {
          description: isLocalArchive
            ? `${t("toast.saved_local")} ${t("toast.donate_nudge")}`
            : t("toast.donate_nudge"),
          action: {
            label: t("toast.archive_action"),
            onClick: () => setStep("archive"),
          },
        },
      );

      const newPlan = isFullPlan
        ? data
        : {
            ...payload,
            id: planId as string,
          };

      if (isManualMode) {
        setIsManualMode(false);
      }

      if (!isFullPlan) {
        setPlans([newPlan as Plan]);
        setCurrentPlanIndex(0);
      }

      // Feedback trigger: 3rd save, then every 5th
      const prevCount = parseInt(
        localStorage.getItem("krs-plan-save-count") || "0",
        10,
      );
      const newCount = prevCount + 1;
      localStorage.setItem("krs-plan-save-count", String(newCount));
      if (onFeedbackTrigger && (newCount === 3 || (newCount > 3 && (newCount - 3) % 5 === 0))) {
        const lastPrompt = parseInt(
          localStorage.getItem("krs-feedback-last-prompt") || "0",
          10,
        );
        if (newCount !== lastPrompt) {
          localStorage.setItem(
            "krs-feedback-last-prompt",
            String(newCount),
          );
          onFeedbackTrigger(newCount);
        }
      }

      setStep("view");
    } catch (err: any) {
      const msg = err.message || "";
      if (msg.includes("Maximum limit of 30 plans")) {
        toast.error(t("toast.archive_full"), {
          action: {
            label: t("toast.archive_action"),
            onClick: () => setStep("archive"),
          },
        });
      } else {
        toast.error(t("toast.save_failed", { error: err.message }));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateManualPlan = (updatedCourses: Course[]) => {
    setPlans((prev) => {
      const next = [...prev];
      if (next[currentPlanIndex]) {
        next[currentPlanIndex] = {
          ...next[currentPlanIndex],
          courses: updatedCourses,
        };
      }
      return next;
    });
  };

  /**
   * Load an archived plan directly into Plotter / Manual Edit mode.
   *
   * Strategy:
   * 1. Extract unique course codes from the plan snapshot.
   * 2. Find every matching section in allMasterCourses (new/updated sections
   *    become available; existing ones are already deduped by id).
   * 3. Always preserve the plan's own snapshot courses so orphaned / removed
   *    DB rows never blank out the inventory.
   * 4. Lock the plan's exact active section IDs so Plotter opens pre-filled.
   * 5. sessionProfile is NEVER modified.
   */
  const handleEditArchived = (
    planInput: any,
    allMasterCourses?: any[],
  ) => {
    const planData: Plan =
      planInput && "data" in planInput && planInput.data
        ? planInput.data
        : (planInput as Plan);
    const planCourses = planData?.courses || [];
    if (planCourses.length === 0) return;

    const codesSet = new Set(planCourses.map((c) => c.code));
    const locked: Record<string, string[]> = {};
    for (const c of planCourses) {
      if (!locked[c.code]) locked[c.code] = [];
      if (!locked[c.code].includes(c.id)) locked[c.code].push(c.id);
    }

    // Gather all available section variations for the plan's course codes from
    // the master catalog. New sections added after the plan was saved appear
    // here automatically so the user can swap to them.
    const masterVariations: Course[] = (allMasterCourses || [])
      .filter((mc) => codesSet.has(mc.code))
      .map((mc) => ({
        ...mc,
        id: mc._id || mc.id || `${mc.code}-${mc.class}`,
      }));

    // Merge: start with master variations, then add any snapshot courses not
    // already present (covers orphaned / cross-prodi / shared plan sections).
    const masterIds = new Set(masterVariations.map((c) => c.id));
    const snapshotOnly = planCourses.filter((c) => !masterIds.has(c.id));
    const merged = [...masterVariations, ...snapshotOnly];

    setCourses(merged);
    setSelectedCodes(Array.from(codesSet));
    setLockedCourses(locked);

    setPlans([planData]);
    setCurrentPlanIndex(0);
    setIsManualMode(true);
    setViewSource("archive");
    setStep("view");
    toast.info(t("toast.edit_mode_loaded"));
  };

  return {
    courses,
    setCourses,
    selectedCodes,
    setSelectedCodes,
    lockedCourses,
    setLockedCourses,
    plans,
    setPlans,
    currentPlanIndex,
    setCurrentPlanIndex,
    viewSource,
    setViewSource,
    planLimit,
    setPlanLimit,
    maxDailySks,
    setMaxDailySks,
    isGenerating,
    isManualMode,
    setIsManualMode,
    isSaving,
    toggleCourse,
    handleDeleteCourse,
    handleAutoLoad,
    handleAddMultipleMasterCourses,
    handleGenerate,
    handleSaveManualPlan,
    handleUpdateManualPlan,
    handleEditArchived,
  };
}
